import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Alert,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import ExtractIcon from '@mui/icons-material/Description';
import AddIcon from '@mui/icons-material/Add';
import { mockEnrichmentOptions } from './mockData';

const ColumnBuilderMock = ({ open, onClose, onAddColumn }) => {
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [customColumn, setCustomColumn] = useState({
    name: '',
    description: '',
    instructions: '',
    dataFormat: 'text',
    useWebSearch: false,
  });
  const [activeTab, setActiveTab] = useState('presets');

  const handlePresetSelect = (preset) => {
    setSelectedPreset(preset);
  };

  const handleAddPreset = () => {
    if (selectedPreset) {
      onAddColumn(selectedPreset);
      onClose();
      setSelectedPreset(null);
    }
  };

  const handleAddCustom = () => {
    if (customColumn.name && customColumn.instructions) {
      const newColumn = {
        ...customColumn,
        id: Date.now(),
        estimatedCredits: customColumn.useWebSearch ? 3 : 1,
      };
      onAddColumn(newColumn);
      onClose();
      setCustomColumn({
        name: '',
        description: '',
        instructions: '',
        dataFormat: 'text',
        useWebSearch: false,
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Add Data Enrichment Column</Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant={activeTab === 'presets' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setActiveTab('presets')}
            >
              Preset Columns
            </Button>
            <Button
              variant={activeTab === 'custom' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setActiveTab('custom')}
            >
              Custom Column
            </Button>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent>
        {activeTab === 'presets' ? (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Choose from pre-configured data columns optimized for common use cases
            </Typography>

            {mockEnrichmentOptions.map((category) => (
              <Accordion key={category.category} defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">{category.category}</Typography>
                  <Chip
                    label={`${category.options.length} options`}
                    size="small"
                    sx={{ ml: 2 }}
                  />
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    {category.options.map((option) => (
                      <Grid item xs={12} md={6} key={option.name}>
                        <Card
                          variant={selectedPreset?.name === option.name ? 'elevation' : 'outlined'}
                          sx={{
                            cursor: 'pointer',
                            border: selectedPreset?.name === option.name ? 2 : 1,
                            borderColor: selectedPreset?.name === option.name ? 'primary.main' : 'divider',
                            '&:hover': {
                              borderColor: 'primary.light',
                              bgcolor: 'action.hover',
                            },
                          }}
                          onClick={() => handlePresetSelect(option)}
                        >
                          <CardContent>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                              <Typography variant="subtitle1" fontWeight="bold">
                                {option.name}
                              </Typography>
                              <Stack direction="row" spacing={1}>
                                {option.useWebSearch ? (
                                  <Chip
                                    icon={<SearchIcon />}
                                    label={`${option.credits} credits`}
                                    size="small"
                                    color="primary"
                                  />
                                ) : (
                                  <Chip
                                    icon={<ExtractIcon />}
                                    label={`${option.credits} credit`}
                                    size="small"
                                    color="default"
                                  />
                                )}
                              </Stack>
                            </Stack>

                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              {option.description}
                            </Typography>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Chip
                                label={option.dataFormat}
                                size="small"
                                variant="outlined"
                              />
                              <Typography variant="body2" color="text.secondary">
                                Example: {option.example}
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create a custom data column with specific extraction instructions
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Column Name"
                  placeholder="e.g., Tech Stack"
                  value={customColumn.name}
                  onChange={(e) => setCustomColumn({ ...customColumn, name: e.target.value })}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Data Format</InputLabel>
                  <Select
                    value={customColumn.dataFormat}
                    onChange={(e) => setCustomColumn({ ...customColumn, dataFormat: e.target.value })}
                    label="Data Format"
                  >
                    <MenuItem value="text">Text</MenuItem>
                    <MenuItem value="number">Number</MenuItem>
                    <MenuItem value="currency">Currency</MenuItem>
                    <MenuItem value="date">Date</MenuItem>
                    <MenuItem value="boolean">Yes/No</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  placeholder="Brief description of what this column contains"
                  value={customColumn.description}
                  onChange={(e) => setCustomColumn({ ...customColumn, description: e.target.value })}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={customColumn.useWebSearch}
                      onChange={(e) => setCustomColumn({ ...customColumn, useWebSearch: e.target.checked })}
                    />
                  }
                  label={
                    <Stack direction="row" spacing={1} alignItems="center">
                      {customColumn.useWebSearch ? (
                        <>
                          <SearchIcon />
                          <Typography>Use Web Search (3 credits per website)</Typography>
                        </>
                      ) : (
                        <>
                          <ExtractIcon />
                          <Typography>Content Extraction (1 credit per website)</Typography>
                        </>
                      )}
                    </Stack>
                  }
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 4 }}>
                  {customColumn.useWebSearch
                    ? 'Search external sources like LinkedIn, news, or company databases'
                    : 'Extract information directly from the website content'
                  }
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Extraction Instructions"
                  placeholder={
                    customColumn.useWebSearch
                      ? "e.g., Search for the company's primary technology stack from job postings and technical blog posts"
                      : "e.g., Look for technology stack information on the careers page, about page, or technical documentation"
                  }
                  value={customColumn.instructions}
                  onChange={(e) => setCustomColumn({ ...customColumn, instructions: e.target.value })}
                  multiline
                  rows={3}
                  required
                  helperText="Be specific about where to look and what format you want the data in"
                />
              </Grid>

              <Grid item xs={12}>
                <Alert severity="info">
                  <Typography variant="body2">
                    <strong>Cost:</strong> {customColumn.useWebSearch ? '3 credits' : '1 credit'} per website
                    <br />
                    <strong>Processing:</strong> {customColumn.useWebSearch ? 'External search' : 'Website content only'}
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose}>
          Cancel
        </Button>
        
        {activeTab === 'presets' ? (
          <Button
            onClick={handleAddPreset}
            variant="contained"
            disabled={!selectedPreset}
            startIcon={<AddIcon />}
          >
            Add Column ({selectedPreset?.credits || 0} credits)
          </Button>
        ) : (
          <Button
            onClick={handleAddCustom}
            variant="contained"
            disabled={!customColumn.name || !customColumn.instructions}
            startIcon={<AddIcon />}
          >
            Add Column ({customColumn.useWebSearch ? 3 : 1} credits)
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ColumnBuilderMock;